// POROS — Resource Readiness (REGULATE). "Do I have everything I need to
// accomplish this objective?" One objective, six fixed categories, any number
// of checkable resources per category. Opened from the orb's Regulate row
// (which passes the objective via setPorosTarget); see olmps/poros-data.ts.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Switch, Text, TextInput, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Check, Plus, X } from 'lucide-react-native';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { SECTORS, SECTOR_LABELS, type Sector } from '../domain/olmps';
import type { ToolProps } from '../olmps/ToolHost';
import { askAgent } from '../live/askAgent';
import {
  POROS_CATS,
  POROS_QUESTION,
  cachePoros,
  emptyPoros,
  getPorosTarget,
  loadPoros,
  mergeSuggestions,
  newItemId,
  parseSuggestions,
  porosKey,
  readiness,
  savePoros,
  suggestPrompt,
  type PorosCat,
  type PorosRecord,
  type PorosTarget,
} from '../olmps/poros-data';
import { Atmos, Chip, Field, GlassCard, GoldButton, Track } from '../v10/ui';
import { ScreenHelp } from '../v10/ScreenHelp';
import { porosHelp } from '../olmps/help/porosHelp';
import { useTheme, makeThemedStyles, DISPLAY_FONT, SECTOR_COLOR } from '../v10/theme';

const LAST_KEY = 'olmps.poros.last';

export function PorosTool({ sector: shellSector }: ToolProps) {
  const styles = useStyles();
  const { t } = useTheme();
  const { session } = useAuth();
  const userId = session?.user.id;

  const [target, setTarget] = useState<PorosTarget | null>(getPorosTarget());
  const [rec, setRec] = useState<PorosRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [filling, setFilling] = useState(false);
  const [draftObjective, setDraftObjective] = useState('');
  const [draftSector, setDraftSector] = useState<Sector>(shellSector && SECTORS.includes(shellSector) ? shellSector : 'P');
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // No orb target: fall back to the last objective used on this device.
  useEffect(() => {
    if (target) {
      AsyncStorage.setItem(LAST_KEY, JSON.stringify(target)).catch(() => {});
      return;
    }
    AsyncStorage.getItem(LAST_KEY)
      .then((raw) => raw && setTarget(JSON.parse(raw) as PorosTarget))
      .catch(() => {});
  }, [target]);

  const key = target ? porosKey(target.stage, target.objective) : null;

  useEffect(() => {
    if (!userId || !target || !key) return;
    let live = true;
    setLoading(true);
    loadPoros(supabase, userId, target.sector, key)
      .then((r) => live && setRec(r ?? emptyPoros(target.objective)))
      .catch((e) => live && setErr(e instanceof Error ? e.message : 'Could not load'))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [userId, target, key]);

  const persist = useCallback(
    (next: PorosRecord) => {
      if (!userId || !target || !key) return;
      cachePoros(target.sector, key, next);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        savePoros(supabase, userId, target.sector, key, next).catch((e) => setErr(e instanceof Error ? e.message : 'Not saved'));
      }, 600);
    },
    [userId, target, key],
  );
  const update = (fn: (r: PorosRecord) => PorosRecord) =>
    setRec((r) => {
      if (!r) return r;
      const next = fn(r);
      persist(next);
      return next;
    });
  const updCat = (cat: PorosCat, fn: (c: PorosRecord['cats'][PorosCat]) => PorosRecord['cats'][PorosCat]) =>
    update((r) => ({ ...r, cats: { ...r.cats, [cat]: fn(r.cats[cat]) } }));

  const autofill = async () => {
    if (!rec || !target) return;
    setFilling(true);
    setErr(null);
    try {
      const r = await askAgent('poros', suggestPrompt(target.objective));
      const sug = parseSuggestions(r.text ?? '');
      if (!sug) throw new Error('POROS did not return a list. Try again.');
      update((cur) => mergeSuggestions(cur, sug));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Auto-fill failed');
    } finally {
      setFilling(false);
    }
  };

  if (!userId) return <Atmos><View style={styles.center}><Text style={styles.muted}>Sign in to use POROS.</Text></View></Atmos>;

  // Pick an objective when opened without one.
  if (!target) {
    return (
      <Atmos>
        <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
          <View style={styles.helpRow}>
            <ScreenHelp help={porosHelp} />
          </View>
          <Text style={styles.question}>{POROS_QUESTION}</Text>
          <Field placeholder="What's the objective?" value={draftObjective} onChangeText={setDraftObjective} maxLength={160} />
          <View style={styles.chips}>
            {SECTORS.map((s) => (
              <Chip key={s} small label={SECTOR_LABELS[s]} active={s === draftSector} color={SECTOR_COLOR[s]} onPress={() => setDraftSector(s)} />
            ))}
          </View>
          <GoldButton label="Check readiness" disabled={!draftObjective.trim()} onPress={() => setTarget({ sector: draftSector, stage: 'S', objective: draftObjective.trim() })} />
        </ScrollView>
      </Atmos>
    );
  }

  const r = readiness(rec);
  const ready = r.total > 0 && r.missing.length === 0;

  return (
    <Atmos>
      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        <View style={styles.helpRow}>
          <ScreenHelp help={porosHelp} />
        </View>
        <GlassCard style={styles.card}>
          <View style={styles.head}>
            <View style={{ flex: 1, gap: 4 }}>
              <Text style={styles.objective}>{target.objective}</Text>
              <Text style={styles.muted}>
                {r.total ? `${r.ready} of ${r.total} required resources ready` : 'No resources listed yet'}
              </Text>
            </View>
            {r.total ? (
              <View style={[styles.badge, { backgroundColor: ready ? 'rgba(156,207,106,0.18)' : 'rgba(230,163,74,0.18)' }]}>
                <Text style={[styles.badgeText, { color: ready ? t.onPace : t.behind }]}>{ready ? 'READY' : 'NOT READY'}</Text>
              </View>
            ) : null}
          </View>
          <Track pct={r.total ? r.ready / r.total : 0} height={6} />
          <GoldButton compact ghost label={filling ? 'POROS is thinking…' : '✦ Auto-fill what this usually needs'} disabled={filling || !rec} onPress={() => void autofill()} style={{ alignSelf: 'flex-start' }} />
          {err ? <Text style={styles.error}>{err}</Text> : null}

          {loading || !rec ? (
            <ActivityIndicator color={t.gold} style={{ marginVertical: 20 }} />
          ) : (
            <View>
              <View style={[styles.row, styles.headerRow]}>
                <Text style={[styles.th, { flex: 1 }]}>CATEGORY</Text>
                <Text style={styles.th}>REQUIRED RESOURCES</Text>
              </View>
              {POROS_CATS.map((c) => {
                const cat = rec.cats[c.key];
                return (
                  <View key={c.key} style={styles.catBlock}>
                    <View style={styles.row}>
                      <Text style={[styles.catLabel, !cat.on && styles.off]} numberOfLines={1}>{c.label}</Text>
                      <View style={styles.catCtl}>
                        <Switch value={cat.on} onValueChange={(v) => updCat(c.key, (x) => ({ ...x, on: v }))} trackColor={{ true: t.onPace, false: t.track }} />
                        <Text style={styles.reqText}>{cat.on ? 'Required' : 'Not needed'}</Text>
                        <Pressable
                          onPress={() => updCat(c.key, (x) => ({ on: true, items: [...x.items, { id: newItemId(), text: '', checked: false }] }))}
                          style={styles.addBtn}
                          accessibilityLabel={`Add to ${c.label}`}
                        >
                          <Plus size={14} color={t.ivory} />
                          <Text style={styles.addText}>Add</Text>
                        </Pressable>
                      </View>
                    </View>
                    {cat.on
                      ? cat.items.map((it) => (
                          <View key={it.id} style={styles.itemRow}>
                            <View style={styles.itemCtl}>
                              <Pressable
                                onPress={() => updCat(c.key, (x) => ({ ...x, items: x.items.map((i) => (i.id === it.id ? { ...i, checked: !i.checked, ai: false } : i)) }))}
                                style={[styles.check, it.checked && { backgroundColor: t.ivory, borderColor: t.ivory }]}
                                accessibilityRole="checkbox"
                                accessibilityState={{ checked: it.checked }}
                              >
                                {it.checked ? <Check size={14} color={t.navy} strokeWidth={3} /> : null}
                              </Pressable>
                              <TextInput
                                style={[styles.itemText, it.checked && styles.itemDone]}
                                value={it.text}
                                autoFocus={!it.text}
                                placeholder="Resource…"
                                placeholderTextColor={t.ivoryFaint}
                                onChangeText={(v) => updCat(c.key, (x) => ({ ...x, items: x.items.map((i) => (i.id === it.id ? { ...i, text: v } : i)) }))}
                                maxLength={120}
                              />
                              <Pressable
                                onPress={() => updCat(c.key, (x) => ({ ...x, items: x.items.filter((i) => i.id !== it.id) }))}
                                hitSlop={8}
                                accessibilityLabel={`Remove ${it.text}`}
                              >
                                <X size={18} color={t.ivorySoft} />
                              </Pressable>
                            </View>
                          </View>
                        ))
                      : null}
                  </View>
                );
              })}
            </View>
          )}
        </GlassCard>
      </ScrollView>
    </Atmos>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
    scroll: { padding: 16, paddingBottom: 60, gap: 12 },
    helpRow: { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: -4 },
    question: { fontFamily: DISPLAY_FONT, fontSize: 18, color: t.ivory, lineHeight: 24 },
    chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    card: { padding: 16, gap: 12 },
    head: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
    objective: { fontFamily: DISPLAY_FONT, fontSize: 21, color: t.ivory, lineHeight: 26 },
    muted: { fontSize: 13.5, color: t.ivorySoft },
    error: { fontSize: 12.5, color: t.lapsed },
    badge: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999 },
    badgeText: { fontSize: 13, fontWeight: '800', letterSpacing: 1 },
    row: { flexDirection: 'row', alignItems: 'center' },
    headerRow: { paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: t.lineFaint },
    th: { fontSize: 11, fontWeight: '700', letterSpacing: 1.2, color: t.ivorySoft },
    catBlock: { borderBottomWidth: 1, borderBottomColor: t.lineFaint, paddingVertical: 8 },
    catLabel: { flex: 1, minWidth: 0, fontSize: 15.5, color: t.ivory, fontWeight: '600' },
    off: { color: t.ivoryFaint },
    catCtl: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    reqText: { width: 74, fontSize: 12, color: t.ivorySoft },
    addBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999, borderWidth: 1, borderColor: t.navyLine },
    addText: { fontSize: 13, color: t.ivory, fontWeight: '600' },
    itemRow: { paddingVertical: 4, paddingLeft: 12 },
    itemCtl: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    check: { width: 24, height: 24, borderRadius: 12, borderWidth: 1.5, borderColor: t.ivorySoft, alignItems: 'center', justifyContent: 'center' },
    itemText: { flex: 1, fontSize: 15, color: t.ivory, paddingVertical: 4 },
    itemDone: { color: t.ivorySoft },
  }),
);
